import type { ColumnsType } from "antd/es/table";
import { Tag } from "antd";
import { formatDate } from "../../shared/utils/date";
import { formatInr } from "../../shared/utils/currency";

export type EntryPageConfig<T> = {
  key: string;
  title: string;
  subtitle: string;
  listUrl: string;
  saveUrl?: string;
  detailUrl?: string;
  deleteUrl?: string;
  importModule?: string;
  permissionPrefix: string;
  columns: ColumnsType<T>;
  supportsBulk?: boolean;
  supportsPendingReport?: boolean;
  readOnly?: boolean;
};

export type DailySummaryRow = {
  id: number;
  paidOfId?: number;
  paymentOfId?: number;
  entryDate?: string;
  dated?: string;
  tranDate?: string;
  paidOfName?: string;
  paidOf?: string;
  paymentOf?: string;
  chequeNo?: string;
  particulars?: string;
  partyName?: string;
  sourceModule?: string;
  voucherType?: string;
  referenceNo?: string;
  narration?: string;
  type?: string;
  entryCategoryName?: string;
  paymentType?: string;
  paymentStatus?: string;
  paymentEntryDate?: string;
  amount?: number;
  overallAmount?: number;
  debit?: number;
  credit?: number;
  balance?: number;
  remarks?: string;
  deletedAt?: string;
  deletedBy?: string;
};

export const dailyEntryConfig: EntryPageConfig<DailySummaryRow> = {
  key: "DailyEntry",
  title: "Daily Entry",
  subtitle: "Summary, details, bulk entry, filters, pending report, and shared right-side lookups.",
  listUrl: "/daily-entries/summary",
  saveUrl: "/daily-entries",
  detailUrl: "/daily-entries",
  deleteUrl: "/daily-entries",
  permissionPrefix: "DailyEntry",
  supportsBulk: true,
  supportsPendingReport: true,
  importModule: "daily-entry",
  columns: [
    { title: "Date", dataIndex: "entryDate", width: 130, render: formatDate },
    { title: "Paid Of", dataIndex: "paidOfName", width: 220 },
    { title: "Entry Category", dataIndex: "entryCategoryName", width: 230 },
    {
      title: "Payment",
      dataIndex: "paymentType",
      width: 120,
      render: (v) => {
        const paymentType = normalizePaymentType(v);
        const color = paymentType === "Bank" ? "blue" : paymentType === "Cash" ? "green" : "default";
        return <Tag color={color}>{paymentType}</Tag>;
      }
    },
    { title: "Status", dataIndex: "paymentStatus", width: 110, render: (v) => {
      const status = normalizePaymentStatus(v);
      const color = status === "Paid" ? "green" : status === "Pending" ? "gold" : status === "Multiple" ? "blue" : status === "-" ? "default" : "red";
      return <Tag color={color}>{status}</Tag>;
    } },
    { title: "Amount", dataIndex: "amount", width: 140, align: "right", render: formatInr }
    ,
    { title: "Overall Amount", dataIndex: "overallAmount", width: 160, align: "right", render: formatInr }
  ]
};

export const cashEntryConfig: EntryPageConfig<DailySummaryRow> = {
  key: "CashEntry",
  title: "Cash Entry",
  subtitle: "Cash-focused entry list using the same fixed entry components.",
  listUrl: "/cash-entries/paged",
  saveUrl: "/cash-entries",
  detailUrl: "/cash-entries",
  deleteUrl: "/cash-entries",
  importModule: "cash-entry",
  permissionPrefix: "CashEntry",
  supportsBulk: true,
  supportsPendingReport: false,
  columns: [
    { title: "Date", dataIndex: "dated", width: 130, render: formatDate },
    {
      title: "Details",
      width: 280,
      render: (_, row) => (
        <div>
          <div>Paid Of: <strong>{row.paidOf || "-"}</strong></div>
          <small>Account: <strong>{row.paymentOf || "-"}</strong></small>
        </div>
      )
    },
    { title: "Debit", dataIndex: "debit", width: 130, align: "right", render: formatInr },
    { title: "Credit", dataIndex: "credit", width: 130, align: "right", render: formatInr },
    { title: "Balance", dataIndex: "balance", width: 130, align: "right", render: formatInr },
    { title: "Remarks", dataIndex: "remarks", width: 260 }
  ]
};

export const statementEntryConfig: EntryPageConfig<DailySummaryRow> = {
  key: "StatementEntry",
  title: "Statement Entry",
  subtitle: "Bank statement entry surface with consistent filters and lookup behavior.",
  listUrl: "/statement-entries/bank-statement",
  saveUrl: "/statement-entries",
  detailUrl: "/statement-entries",
  deleteUrl: "/statement-entries",
  importModule: "statement-entry",
  permissionPrefix: "StatementEntry",
  supportsBulk: true,
  readOnly: true,
  supportsPendingReport: false,
  columns: [
    { title: "Date", dataIndex: "entryDate", width: 130, render: (_, row) => formatDate(row.entryDate ?? row.tranDate) },
    { title: "Source", width: 170, render: (_, row) => <Tag>{row.sourceModule ?? row.voucherType ?? row.type ?? "-"}</Tag> },
    { title: "Particulars", dataIndex: "particulars", width: 240 },
    { title: "Party", width: 180, render: (_, row) => row.partyName ?? row.paidOf ?? "-" },
    { title: "Ref No", dataIndex: "referenceNo", width: 140 },
    { title: "Received", dataIndex: "debit", width: 130, align: "right", render: formatInr },
    { title: "Paid", dataIndex: "credit", width: 130, align: "right", render: formatInr },
    { title: "Balance", dataIndex: "balance", width: 140, align: "right", render: formatInr },
    { title: "Narration", dataIndex: "narration", width: 260 }
  ]
};

export const statementManualColumns: ColumnsType<DailySummaryRow> = [
  { title: "Date", dataIndex: "tranDate", width: 130, render: formatDate },
  {
    title: "Details",
    width: 300,
    render: (_, row) => (
      <div>
        <div>Account: <strong>{row.chequeNo || "-"}</strong></div>
        <small>Particulars: <strong>{row.particulars || "-"}</strong></small>
      </div>
    )
  },
  { title: "Party", dataIndex: "partyName", width: 180 },
  { title: "Debit", dataIndex: "debit", width: 130, align: "right", render: formatInr },
  { title: "Credit", dataIndex: "credit", width: 130, align: "right", render: formatInr },
  { title: "Balance", dataIndex: "balance", width: 140, align: "right", render: formatInr },
  { title: "Narration", dataIndex: "narration", width: 260 },
  { title: "Type", dataIndex: "type", width: 110, render: (v) => <Tag color={v === "Credit" ? "green" : "red"}>{v ?? "-"}</Tag> }
];

function normalizePaymentStatus(value: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  if (value === "Multiple") return "Multiple";
  if (value === 2 || value === "2" || value === "Paid") return "Paid";
  if (value === 3 || value === "3" || value === "Failed") return "Failed";
  if (value === 4 || value === "4" || value === "Cancelled") return "Cancelled";
  return "Pending";
}

function normalizePaymentType(value: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  if (value === "Multiple") return "Multiple";
  if (value === 2 || value === "2" || value === "Bank") return "Bank";
  return "Cash";
}
